import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sparkles, Mail, Share2, RefreshCw, TrendingUp, AlertCircle, Settings as SettingsIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

export const UsageAnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPlatformOwner = user?.role === 'super_admin';
  const isCountryOperator = user?.role === 'country_operator';
  const planLower = (user?.plan || '').toLowerCase();
  const isTopTier = planLower === 'enterprise';

  const [usage, setUsage] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usageRes, perfRes] = await Promise.all([
        axios.get(`${API_URL}/api/usage/overview`),
        axios.get(`${API_URL}/api/usage/content-performance`),
      ]);
      setUsage(usageRes.data);
      setPerformance(perfRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-500';
    if (percentage >= 70) return 'text-amber-500';
    return 'text-emerald-brand';
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-emerald-brand';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  const UsageCard = ({ title, icon: Icon, used, limit, percentage }) => {
    const isUnlimited = limit === -1;
    return (
      <Card className="bg-white border-gray-border">
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-brand/10 flex items-center justify-center flex-shrink-0">
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-brand" />
              </div>
              <h3 className="text-sm font-medium text-text-primary truncate">{title}</h3>
            </div>
            {!isUnlimited && percentage >= 80 && (
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between flex-wrap gap-1">
              <span className={cn("text-lg sm:text-2xl font-bold", getUsageColor(percentage))}>
                {used.toLocaleString()}
              </span>
              <span className="text-xs sm:text-sm text-gray-medium">
                / {isUnlimited ? 'Unlimited' : limit.toLocaleString()}
              </span>
            </div>
            {!isUnlimited && (
              <Progress 
                value={percentage} 
                className={cn("h-2", getProgressColor(percentage))}
              />
            )}
            {isUnlimited && (
              <Badge className="bg-emerald-brand/10 text-emerald-brand">
                {isCountryOperator ? 'Operator - Credit Pool' : (isPlatformOwner ? 'Platform - Unlimited' : 'Enterprise - Unlimited')}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="usage-analytics-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Usage Analytics<PageInfoTooltip pageKey="usage-analytics" /></h1>
        <p className="text-gray-medium">
          Track your AI credit consumption and content performance • {usage?.current_month}
        </p>
      </div>

      {/* Current Plan Badge — semantics are role-aware:
          • super_admin: "Platform Owner" + a "Platform Administration" link, never an "Upgrade" CTA.
          • enterprise (top tier subscribers): "Manage Plan" link (cannot upgrade higher).
          • starter / growth subscribers: "Upgrade for More Credits" CTA.       */}
      <Card className="bg-gradient-to-r from-emerald-brand to-emerald-hover border-0">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-white" />
            <div>
              <p className="text-white/80 text-sm">
                {isPlatformOwner ? 'Account Type' : (isCountryOperator ? 'Operator Account' : 'Current Plan')}
              </p>
              <p className="text-white font-heading font-bold text-lg capitalize">
                {isPlatformOwner ? 'Platform Owner' : (isCountryOperator ? 'Country Operator' : (usage?.plan || 'Starter'))}
              </p>
            </div>
          </div>
          {isPlatformOwner ? (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/20 text-white hover:bg-white/30 border-0"
              onClick={() => navigate('/admin')}
              data-testid="platform-admin-link"
            >
              <SettingsIcon className="h-3.5 w-3.5 mr-1.5" />
              Platform Administration
            </Button>
          ) : isCountryOperator ? (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/20 text-white hover:bg-white/30 border-0"
              onClick={() => navigate('/settings/credits')}
              data-testid="top-up-credits-link"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Top Up Credits
            </Button>
          ) : (isTopTier && user?.role !== 'team_member') ? (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/20 text-white hover:bg-white/30 border-0"
              onClick={() => navigate('/settings')}
              data-testid="manage-plan-link"
            >
              <SettingsIcon className="h-3.5 w-3.5 mr-1.5" />
              Manage Subscription
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/20 text-white hover:bg-white/30 border-0"
              onClick={() => navigate('/settings')}
              data-testid="upgrade-link"
            >
              Upgrade for More Credits
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Usage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <UsageCard 
          title="AI Generations" 
          icon={Sparkles}
          used={usage?.ai_generations?.used || 0}
          limit={usage?.ai_generations?.limit || 100}
          percentage={usage?.ai_generations?.percentage || 0}
        />
        <UsageCard 
          title="Emails Sent" 
          icon={Mail}
          used={usage?.emails_sent?.used || 0}
          limit={usage?.emails_sent?.limit || 5000}
          percentage={usage?.emails_sent?.percentage || 0}
        />
        <UsageCard 
          title="Social Posts" 
          icon={Share2}
          used={usage?.social_posts?.used || 0}
          limit={usage?.social_posts?.limit || 50}
          percentage={usage?.social_posts?.percentage || 0}
        />
      </div>

      {/* Content Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content Type Breakdown */}
        <Card className="bg-white border-gray-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Content Type Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {performance?.content_types?.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={performance.content_types}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="type"
                    >
                      {performance.content_types.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-medium">
                <div className="text-center">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No content generated yet</p>
                  <p className="text-sm">Start creating with AI to see your breakdown</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performing Content */}
        <Card className="bg-white border-gray-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-brand" />
              Top Performing Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            {performance?.top_performing?.length > 0 ? (
              <div className="space-y-4">
                {performance.top_performing.map((content, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-text-primary">{content.content}</p>
                      <Badge variant="secondary" className="mt-1 capitalize">{content.type}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-brand">{content.engagement}%</p>
                      <p className="text-xs text-gray-medium">Engagement</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-medium">
                <p>Performance data will appear as you create content</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UsageAnalyticsPage;
